import { defer, Observable } from 'rxjs';
import { ajax, AjaxConfig, AjaxResponse } from 'rxjs/ajax';
import { finalize, map } from 'rxjs/operators';

import { AppStore } from 'app/store';
import { Model } from 'app/type/model';
import { startLoading, stopLoading } from 'features/top-loader/loader.slice';

import { HttpHandler } from './http-handler';
import { Interceptor } from './interceptor';
import { HttpOptions, RequestParams } from '../services/http-options';

/**
 * Basic http client wrapper
 */
class HttpClient {
    private store!: AppStore;
    private interceptors: Interceptor[] = [];

    public setStore(store: AppStore) {
        this.store = store;
    }

    public processInterceptors<T>(
        request: AjaxConfig,
        handler: HttpHandler<T>,
        index = 0,
    ): Observable<AjaxResponse<T>> {
        if (index === this.interceptors.length) {
            return handler.handle(request);
        }

        const nextHandler: HttpHandler<T> = {
            handle: (req: AjaxConfig): Observable<AjaxResponse<T>> => this.processInterceptors(req, handler, index + 1),
        };

        return this.interceptors[index].intercept(this.store, request, nextHandler);
    }

    public request<R>(method: string, url: string, options?: HttpOptions): Observable<Model<R>> {
        if (options?.params) {
            const nvp: string[] = HttpClient.parseParams(options.params);

            url += `?${nvp.join('&')}`;

            if (options.params.download) {
                options.download = true;
            }
        }

        const originalRequest: AjaxConfig = {
            method,
            url,
            body: options?.body,
            headers: {
                'Content-Type': 'application/json',
                ...options?.headers,
            },
            responseType: options?.download ? 'blob' : 'json',
        };

        const mainHandler: HttpHandler<Model<R>> = {
            handle: (request: AjaxConfig): Observable<AjaxResponse<Model<R>>> =>
                defer(() => {
                    this.store.dispatch(startLoading());

                    return ajax<Model<R>>(request);
                }).pipe(
                    finalize(() => {
                        this.store.dispatch(stopLoading());
                    }),
                ),
        };

        return this.processInterceptors(originalRequest, mainHandler).pipe(map((resp) => resp.response));
    }

    public delete<R = void>(url: string, options?: HttpOptions): Observable<Model<R>> {
        return this.request('DELETE', url, options);
    }

    public get<R = void>(url: string, options?: HttpOptions): Observable<Model<R>> {
        return this.request('GET', url, options);
    }

    public patch<R = void>(url: string, body?: object, options?: HttpOptions): Observable<Model<R>> {
        return this.request('PATCH', url, { ...options, body });
    }

    public post<R = void>(url: string, body?: object, options?: HttpOptions): Observable<Model<R>> {
        return this.request('POST', url, { ...options, body });
    }

    public put<R = void>(url: string, body?: object, options?: HttpOptions): Observable<Model<R>> {
        return this.request('PUT', url, { ...options, body });
    }

    public addInterceptor(interceptor: Interceptor) {
        this.interceptors.push(interceptor);
    }

    private static parseParams(params: RequestParams, prefix = '', suffix = '') {
        const nvp: string[] = [];
        for (const key of Object.keys(params)) {
            const name = prefix + key + suffix;
            const data = params[key];

            if (undefined === data) {
                // do not push undefined params
                continue;
            }

            if ('object' === typeof data) {
                nvp.push(...HttpClient.parseParams(params[key] as RequestParams, `${name}[`, ']'));
                continue;
            }

            nvp.push(`${encodeURIComponent(name)}=${encodeURIComponent(data)}`);
        }

        return nvp;
    }
}

export const httpClient = new HttpClient();
